'use client';

import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import QuizStepAge from './QuizStepAge';
import QuizStepChallenge from './QuizStepChallenge';
import { IconArrowBackUp, IconX } from '@tabler/icons-react';
import type { ProgrammeWithCategories } from '@dr-pam/common-components/Services/Server/ProgrammeService';
import type { CategoryWithSubCategories } from '@dr-pam/common-components/Services/Server/CategoryService';
import Loading from '@dr-pam/common-components/Components/Misc/Loading';
import FetchUtils from '@dr-pam/common-components/Utils/FetchUtils';

interface QuizFormProps {
	onClose: () => void;
	data?: ProgrammeWithCategories;
}

const areLeaves = (nodes: CategoryWithSubCategories[]): boolean =>
	nodes.every((n) => (n.subCategories?.length ?? 0) === 0);

export default function QuizForm({ data: initialData, onClose }: QuizFormProps) {
	const [data, setData] = useState<ProgrammeWithCategories | undefined>(initialData);
	const [isLoading, setIsLoading] = useState(!initialData);
	const [error, setError] = useState<string | null>(null);

	const [currentStep, setCurrentStep] = useState(0);
	const [question, setQuestion] = useState<string | undefined>(initialData?.categories[0]?.name);
	const [options, setOptions] = useState<CategoryWithSubCategories[]>([]);
	const [rootSlug, setRootSlug] = useState<string | undefined>();
	const [isFinalStep, setIsFinalStep] = useState(false);

	const router = useRouter();

	// lazy-load quiz data if not provided
	useEffect(() => {
		if (data) {
			const root = data.categories[0];
			setQuestion(root?.name);
			setOptions(root?.subCategories ?? []);
			setIsFinalStep(false);
			return;
		}

		let cancelled = false;
		const loadQuiz = async () => {
			setIsLoading(true);
			setError(null);

			const request = FetchUtils.getJson<ProgrammeWithCategories>('/find-essentials-quiz');

			try {
				const quiz = await request.response;

				if (cancelled) {
					return;
				}

				setData(quiz);

				const root = quiz.categories[0];
				setQuestion(root?.name);
				setOptions(root?.subCategories ?? []);
				setIsFinalStep(false);
			} catch (err) {
				if (cancelled) {
					return;
				}
				console.error(err);
				setError('Unable to load quiz.');
			} finally {
				if (!cancelled) {
					setIsLoading(false);
				}
			}
		};

		void loadQuiz();

		return () => {
			cancelled = true;
		};
	}, [data]);

	const goToResults = (challengeSlug: string) => {
		if (rootSlug) {
			router.push(`/find-essentials/${rootSlug}/${challengeSlug}`);
		} else {
			router.push(`/find-essentials/${challengeSlug}`);
		}
	};

	const onOptionSelected = (selected: CategoryWithSubCategories) => {
		if (!data) {
			return;
		}

		if (!rootSlug && currentStep === 0) {
			setRootSlug(selected.slug);
		}

		const children = selected.subCategories ?? [];

		if (children.length === 0) {
			goToResults(selected.slug);
			return;
		}
		const childWithGrandchildren = children.find((child) => (child.subCategories?.length ?? 0) > 0);

		if (childWithGrandchildren) {
			const nextOptions = childWithGrandchildren.subCategories ?? [];
			setQuestion(childWithGrandchildren.name);
			setOptions(nextOptions);
			setCurrentStep((step) => step + 1);
			setIsFinalStep(areLeaves(nextOptions));
			return;
		}
		const nextOptions = children;
		setQuestion(selected.name);
		setOptions(nextOptions);
		setCurrentStep((step) => step + 1);
		setIsFinalStep(areLeaves(nextOptions));
	};

	const handleBackPressed = () => {
		if (!data) {
			return;
		}
		const root = data.categories[0];
		setCurrentStep(0);
		setQuestion(root?.name);
		setOptions(root?.subCategories ?? []);
		setRootSlug(undefined);
		setIsFinalStep(false);
	};

	const getTitle = () => {
		if (!question) {
			return '';
		}
		return question.charAt(0).toUpperCase() + question.slice(1);
	};

	return (
		<div className="quiz-form-container with-background">
			<div className="quiz-header">
				<div className="flex jb ac w100">
					<IconArrowBackUp
						className={`controller ${currentStep === 0 ? 'invisible' : ''}`}
						onClick={handleBackPressed}
					/>
					<IconX className="controller" onClick={onClose} />
				</div>
			</div>

			{isLoading && (
				<div className="quiz-loading-center flex jc ac">
					<Loading size="medium" />
				</div>
			)}

			{!isLoading && !error && (
				<>
					<h2 className="title">{getTitle()}</h2>

					{isFinalStep && rootSlug ? (
						<QuizStepChallenge ageSlug={rootSlug} challenges={options} />
					) : (
						<QuizStepAge onSelected={onOptionSelected} data={options} />
					)}
				</>
			)}
		</div>
	);
}
